// Erişim kontrolü ve trial yönetimi utility fonksiyonları

// Firestore Timestamp'i Date'e çevir
const toDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  
  // Zaten Date ise
  if (dateValue instanceof Date) return dateValue;
  
  // Firestore Timestamp ise
  if (dateValue.toDate && typeof dateValue.toDate === 'function') {
    return dateValue.toDate();
  }
  
  // String veya number ise
  if (typeof dateValue === 'string' || typeof dateValue === 'number') {
    return new Date(dateValue);
  }
  
  // Timestamp objesi (seconds ve nanoseconds)
  if (dateValue.seconds) {
    return new Date(dateValue.seconds * 1000 + (dateValue.nanoseconds || 0) / 1000000);
  }
  
  return null;
};

/** Premium bitiş tarihi — Unix saniye (IBAN) veya ISO / Timestamp */
const parsePeriodEnd = (dateValue: any): Date | null => {
  if (dateValue === null || dateValue === undefined || dateValue === '') return null;
  if (typeof dateValue === 'number') {
    return new Date(dateValue > 1e12 ? dateValue : dateValue * 1000);
  }
  return toDate(dateValue);
};

/** Yeni kayıtlar için varsayılan ücretsiz deneme süresi (gün) */
export const TRIAL_DAYS = 30;
export const TRIAL_MS = TRIAL_DAYS * 24 * 60 * 60 * 1000;

/** Premium abonelik süresi dolmamışsa true (IBAN/dekont ve Stripe) */
export const isPremiumActive = (userData: any): boolean => {
  if (!userData?.hasActiveSubscription) return false;
  const periodEnd = parsePeriodEnd(userData.currentPeriodEnd);
  if (!periodEnd) return true;
  return new Date() < periodEnd;
};

/** currentPeriodEnd geçmiş premium — Firestore senkronu için */
export const isSubscriptionExpired = (userData: any): boolean => {
  if (!userData?.hasActiveSubscription) return false;
  const periodEnd = parsePeriodEnd(userData.currentPeriodEnd);
  if (!periodEnd) return false;
  return new Date() >= periodEnd;
};

const getEffectiveTrialEndDate = (userData: any): Date | null => {
  if (!userData) return null;

  const start = toDate(userData.trialStartDate) ?? toDate(userData.createdAt);
  if (!start) return null;

  const maxEnd = new Date(start.getTime() + TRIAL_MS);
  const directEnd = toDate(userData.trialEndDate);
  if (directEnd) {
    // Admin tarafından uzatılmış trial'ları kabul et; yoksa varsayılan süre (TRIAL_DAYS)
    return directEnd;
  }

  return maxEnd;
};

export const hasAccess = (userData: any): boolean => {
  if (!userData) return false;

  // Admin her zaman erişebilir
  if (userData.isAdmin) return true;

  if (isPremiumActive(userData)) return true;

  const trialEnd = getEffectiveTrialEndDate(userData);
  if (!trialEnd) {
    // Tarih yoksa yeni kullanıcı varsay
    return true;
  }

  const now = new Date();
  return now < trialEnd;
};

export const getTrialDaysRemaining = (userData: any): number => {
  const trialEnd = getEffectiveTrialEndDate(userData);
  if (!trialEnd) return 0;
  
  const now = new Date();
  const diff = trialEnd.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  
  return days > 0 ? days : 0;
};

// Geri sayım için detaylı zaman bilgisi (getEffectiveTrialEndDate kullanır)
export const getTrialTimeRemaining = (userData: any): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number; // Toplam milisaniye
} => {
  const trialEnd = getEffectiveTrialEndDate(userData);
  if (!trialEnd) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }
  
  const now = new Date();
  const diff = trialEnd.getTime() - now.getTime();
  
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
  }
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { days, hours, minutes, seconds, total: diff };
};

export const isTrialActive = (userData: any): boolean => {
  if (!userData) return false;
  if (isPremiumActive(userData)) return false;
  
  return getTrialDaysRemaining(userData) > 0;
};

export const getTrialStatus = (userData: any): {
  isActive: boolean;
  daysRemaining: number;
  endDate: Date | null;
} => {
  if (!userData) {
    return { isActive: false, daysRemaining: 0, endDate: null };
  }
  
  const daysRemaining = getTrialDaysRemaining(userData);
  const endDate = toDate(userData.trialEndDate);
  
  return {
    isActive: isTrialActive(userData),
    daysRemaining,
    endDate
  };
};

